import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { ChevronLeft, FileDown, Loader2, Trash2, Upload, X } from "lucide-react";
import { useChatPendingInputStore } from "@/stores/chatPendingInputStore";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ncoder/ui";
import {
  useAllFindings,
  useCancelIngest,
  useDeleteReleaseDoc,
  useFindings,
  useOngoingReleaseJob,
  useOrgMetadataTypes,
  usePipelineStream,
  useReleaseDocsList,
  useReleaseReportCsv,
  useReleaseReportJson,
  useUploadReleaseDoc,
  type ImpactBucket,
} from "@/hooks/salesforce-release";
import { ImpactMetrics } from "@/components/molecules/salesforce-release/ImpactMetrics";
import { ImpactSeverityRanked } from "@/components/molecules/salesforce-release/ImpactSeverityRanked";
import { ReleaseImpactContent } from "@/components/organisms/salesforce-release/ReleaseImpactContent";
import { MetadataTypeMultiSelect } from "@/components/atoms/salesforce-release/MetadataTypeMultiSelect";
import type { ComponentImproveArg } from "@/components/atoms/salesforce-release/ComponentRow";
import { getMetadataItemByName, getMetadataItem } from "@/pages/multi-agent/ide/panels/resolveDependencyItemForEditor";
import { useMultiAgentIdeStore } from "@/pages/multi-agent/ide/store/multiAgentIdeStore";
import { useActivityStore } from "@/stores/activityStore";

export function SalesforceReleaseLayout() {
  const docsQ = useReleaseDocsList();
  const docs = docsQ.data ?? [];
  const [activeReleaseId, setActiveReleaseId] = useState<string | null>(null);

  // Detect ongoing pipeline job — fires on mount and on window focus so a
  // page refresh reconnects the SSE stream automatically.
  const ongoingJobQ = useOngoingReleaseJob();
  const ongoingJob = ongoingJobQ.data ?? null;

  // Priority: ongoing job > first doc in list.
  // When an ongoing job is found, switch to its release_id so the stream reconnects.
  useEffect(() => {
    if (ongoingJob?.job_id) {
      setActiveReleaseId(ongoingJob.job_id);
    }
  }, [ongoingJob?.job_id]);

  // Pre-seed the ActivityStore as soon as an ongoing job is detected so the
  // ActivityDropdown shows a running indicator immediately — before the SSE
  // stream reconnects and the first event arrives (which normally triggers
  // startActivity). The ID format must match what releasePipelineStore uses
  // (`release_pipeline:${releaseId}`) so the SSE-driven updates land on the
  // same activity entry and don't create a duplicate.
  useEffect(() => {
    if (!ongoingJob?.job_id) return;
    const activityId = `release_pipeline:${ongoingJob.job_id}`;
    useActivityStore.getState().startActivity(
      activityId,
      'release_pipeline',
      'Reconnecting to pipeline…',
      { release_id: ongoingJob.job_id },
    );
  }, [ongoingJob?.job_id]);

  useEffect(() => {
    if (activeReleaseId || docs.length === 0) return;
    setActiveReleaseId(docs[0].release_id);
  }, [activeReleaseId, docs]);

  const activeDoc = useMemo(
    () => docs.find((d) => d.release_id === activeReleaseId) ?? null,
    [docs, activeReleaseId],
  );

  const upload = useUploadReleaseDoc();
  const cancelMut = useCancelIngest();
  const deleteMut = useDeleteReleaseDoc();
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Delete the currently-selected release doc (cascade-removes its chunks + scans
  // server-side). Confirm first — this is destructive and un-undoable — then move
  // the selection to another doc (or none) so the view never points at a gone id.
  const handleDeleteActiveDoc = async () => {
    if (!activeDoc) return;
    const ok = window.confirm(
      `Delete "${activeDoc.filename}"?\n\nThis permanently removes the release document and its scan results. This cannot be undone.`,
    );
    if (!ok) return;
    const deletedId = activeDoc.release_id;
    try {
      await deleteMut.mutateAsync(deletedId);
      toast.success("Release document deleted");
      const next = docs.find((d) => d.release_id !== deletedId) ?? null;
      setActiveReleaseId(next?.release_id ?? null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not delete release document");
    }
  };
  const findingsRef = useRef<HTMLDivElement | null>(null);
  const overviewRef = useRef<HTMLDivElement | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBucket, setSelectedBucket] = useState<string>("all");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showFindings, setShowFindings] = useState(false);

  useEffect(() => { setPage(1); }, [selectedBucket, selectedTypes, activeReleaseId, pageSize]);
  useEffect(() => { setShowFindings(false); setSelectedBucket("all"); setSelectedTypes([]); }, [activeReleaseId]);

  const selectedTypeSingle =
    selectedTypes.length === 0 ? "all" : selectedTypes.length === 1 ? selectedTypes[0] : "all";

  // Unified pipeline stream — covers document processing + scan in one SSE
  const pipeline = usePipelineStream(activeReleaseId);

  // Findings from dedicated endpoint; enabled once activeReleaseId is set
  const findingsData = useFindings(activeReleaseId, {
    bucket: selectedBucket !== "all" ? (selectedBucket as ImpactBucket) : undefined,
    componentTypes: selectedTypes.length > 0 ? selectedTypes : undefined,
    page,
    pageSize,
  });

  const findings = findingsData.findings;
  const totalFindings = findingsData.summary?.total_findings ?? findings.length;
  const totalPages = findingsData.summary?.total_pages ?? 1;
  // scan_id: prefer findings response, fall back to document record
  const scanId = findingsData.scanId ?? activeDoc?.scan_id ?? null;

  // Unfiltered, full-scan findings for the heatmap — decoupled from the paginated
  // `findings` above (which is capped at `pageSize` and reflects the CURRENT page +
  // active bucket/type filters). The heatmap must always summarise the WHOLE scan,
  // so it stays disabled until `totalFindings` is known, then pages through the
  // backend (capped at 200/page) and merges the results.
  const heatmapData = useAllFindings(activeReleaseId, totalFindings);
  const heatmapLoading = totalFindings > 0 && heatmapData.isLoading;

  // Normalise bucket counts from capitalized API keys → BucketCounts shape
  const normalizedBucketCounts = useMemo(() => {
    const bc = findingsData.summary?.bucket_counts;
    if (!bc) return undefined;
    return {
      breaking: bc["Breaking"] ?? 0,
      deprecated: bc["Deprecated"] ?? 0,
      review: bc["Review"] ?? 0,
      improvement: bc["Improvement"] ?? 0,
      ok: bc["OK"] ?? 0,
    };
  }, [findingsData.summary]);

  const totalComponents = useMemo(() => {
    const s = new Set<string>();
    for (const f of findings) s.add(f.component_ref);
    return s.size;
  }, [findings]);

  // Build heatmap from the full unfiltered scan (component_type × bucket), not
  // the paginated table `findings` — otherwise it only reflects whichever page
  // happens to be loaded.
  const severityHeatmap = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    for (const f of heatmapData.findings) {
      if (!map[f.component_type]) map[f.component_type] = {};
      map[f.component_type][f.bucket] = (map[f.component_type][f.bucket] ?? 0) + 1;
    }
    return map;
  }, [heatmapData.findings]);

  const orgTypesQ = useOrgMetadataTypes();
  const componentTypes = useMemo(() => {
    const set = new Set<string>([
      ...(orgTypesQ.data ?? []),
      ...(findingsData.availableFilters?.component_types ?? []),
      ...findings.map((f) => f.component_type),
    ]);
    set.delete("ReleaseNote");
    return Array.from(set).sort();
  }, [orgTypesQ.data, findingsData.availableFilters, findings]);

  const defaultScanTypes = useMemo(() => {
    const s = new Set<string>(findingsData.availableFilters?.component_types ?? Object.keys(severityHeatmap));
    s.delete("ReleaseNote");
    return s;
  }, [findingsData.availableFilters, severityHeatmap]);

  const isInitialLoading = docsQ.isLoading || (activeReleaseId !== null && findingsData.isLoading);

  const visibleFindings = useMemo(() => {
    if (!searchTerm.trim()) return findings;
    const q = searchTerm.toLowerCase();
    return findings.filter((f) =>
      f.component_name.toLowerCase().includes(q) ||
      f.component_ref.toLowerCase().includes(q) ||
      (f.rationale ?? "").toLowerCase().includes(q) ||
      (f.fix_action ?? "").toLowerCase().includes(q) ||
      (f.deprecated_or_changed_symbol ?? "").toLowerCase().includes(q),
    );
  }, [findings, searchTerm]);

  const onUpload = async (file: File) => {
    try {
      const result = await upload.mutateAsync(file);
      setActiveReleaseId(result.release_id);
      if (result.deduped) toast("This file was already uploaded — reusing previous ingest.", { icon: "♻️" });
    } catch (err: unknown) {
      toast.error(`Upload failed: ${(err as Error)?.message ?? String(err)}`);
    }
  };

  const onCancel = () => {
    if (!activeReleaseId) return;
    cancelMut.mutateAsync(activeReleaseId).catch((e) =>
      toast.error(`Cancel failed: ${(e as Error)?.message ?? String(e)}`),
    );
  };

  const csvMut = useReleaseReportCsv();
  const jsonMut = useReleaseReportJson();
  const exportFilenameBase = useMemo(() => {
    if (!scanId) return undefined;
    const docName = activeDoc?.filename?.replace(/\.[^.]+$/, "") ?? scanId.slice(0, 8);
    return `salesforce-release-impact-${docName}-${scanId.slice(0, 8)}`;
  }, [scanId, activeDoc]);

  const doExport = (kind: "csv" | "json") => {
    if (!scanId) { toast.error("No completed scan to export yet."); return; }
    const args = {
      scanId,
      filenameBase: exportFilenameBase,
      bucket: selectedBucket !== "all" ? (selectedBucket as ImpactBucket) : undefined,
      componentTypes: selectedTypes.length > 0 ? selectedTypes : undefined,
    };
    (kind === "csv" ? csvMut : jsonMut).mutateAsync(args).catch((e) =>
      toast.error(`Export failed: ${(e as Error)?.message ?? String(e)}`),
    );
  };

  // "Improve with AI" — gather the release-impact scan into a prompt and hand
  // it to the multi-agent chat (mirrors the security scanner handoff, targeting
  // /multi-agent). MultiAgentRoute consumes `multiAgentIntent` on mount.
  const navigate = useNavigate();
  const setMultiAgentIntent = useChatPendingInputStore((s) => s.setMultiAgentIntent);
  const releaseName = activeDoc?.filename ?? "this release";
  const canImprove = !!scanId && totalFindings > 0;

  const improveWithAI = () => {
    const bc = normalizedBucketCounts;
    const lines: string[] = [];
    lines.push(`Help me act on the Salesforce release-impact scan for "${releaseName}".`);
    lines.push("");
    if (bc) {
      lines.push(
        `Impact summary: ${bc.breaking} breaking, ${bc.deprecated} deprecated, ` +
          `${bc.review} to review, ${bc.improvement} improvement(s) across ${totalComponents} component(s).`,
      );
      lines.push("");
    }
    // Prioritise the most severe loaded findings.
    const order: Record<string, number> = { Breaking: 0, Deprecated: 1, Review: 2, Improvement: 3, OK: 4 };
    const top = [...findings]
      .sort((a, b) => (order[a.bucket] ?? 9) - (order[b.bucket] ?? 9))
      .slice(0, 15);
    if (top.length) {
      lines.push("Top impacted components:");
      for (const f of top) {
        const sym = f.deprecated_or_changed_symbol ? ` (${f.deprecated_or_changed_symbol})` : "";
        const fix = f.fix_action ? ` — fix: ${f.fix_action}` : "";
        lines.push(`- [${f.bucket}] ${f.component_type} ${f.component_name}${sym}${fix}`);
      }
      lines.push("");
    }
    lines.push(
      "For each breaking/deprecated item, explain the impact in this org and propose a concrete fix, prioritised by severity.",
    );
    setMultiAgentIntent(lines.join("\n"));
    navigate("/weave");
  };

  // Per-component "Fix with AI" — one finding row at a time. Takes that
  // component (the class) + its findings into the multi-agent chat to fix,
  // mirroring the security scanner's per-item handoff.
  const improveComponentWithAI = async (g: ComponentImproveArg) => {
    // Flatten newlines (no regex per repo rule) so each detail stays on one line.
    const flat = (v: string | number | null | undefined): string => {
      let out = "";
      for (const ch of String(v ?? "")) out += ch === "\n" || ch === "\r" ? " " : ch;
      return out.trim();
    };
    // Resolve the class to OUR DB metadata item (UUID + source) and seed it as the
    // multi-agent refactor target — so the agent loads the EXACT class by id and
    // does NOT search/re-fetch to locate it (which consumed huge tokens).
    // Best-effort: falls back to a name-only "read its full source" handoff.
    let seeded = false;
    if (g.componentType === "ApexClass" || g.componentType === "ApexTrigger") {
      try {
        const byName = await getMetadataItemByName(g.componentName, undefined, true);
        const id = byName?.id;
        if (id) {
          let content = (byName as { content?: string })?.content || "";
          if (!content) {
            const full = await getMetadataItem(id, undefined, true).catch(() => null);
            content = full?.content || "";
          }
          useMultiAgentIdeStore.getState().setPendingRefactorFile({
            id,
            name: g.componentName,
            content,
            metadataType: g.componentType,
          });
          seeded = true;
        }
      } catch {
        // fall through to the name-only handoff
      }
    }

    const n = g.findings.length;
    const lines: string[] = [];
    lines.push(
      `Help me fix the Salesforce release-impact findings for the ${g.componentType} ` +
        `"${g.componentName}" (from the "${releaseName}" release-readiness scan).`,
    );
    lines.push("");
    lines.push(
      seeded
        ? `The ${g.componentType} "${g.componentName}" is already loaded as the refactor ` +
            `target (its exact source + id are attached). Do NOT search for or re-fetch the ` +
            `class — use the attached source and address the ${n} finding${n === 1 ? "" : "s"} below.`
        : `Open ${g.componentName} (${g.componentType}), read its full source, then address ` +
            `the ${n} finding${n === 1 ? "" : "s"} below.`,
    );
    // One clean block per finding — reads well as plain text AND markdown
    // (no pipe table, which renders as a wall of "|" when the bubble is raw text).
    g.findings.forEach((f, i) => {
      const conf = f.confidence ? ` (confidence: ${f.confidence})` : "";
      lines.push("");
      lines.push(`Finding ${i + 1} — ${flat(f.bucket)}${conf}`);
      if (f.lines && f.lines !== "n/a") lines.push(`- Lines: ${flat(f.lines)}`);
      if (f.deprecated_or_changed_symbol)
        lines.push(`- Changed / deprecated: ${flat(f.deprecated_or_changed_symbol)}`);
      if (f.rationale) lines.push(`- What & why: ${flat(f.rationale)}`);
      const fix = f.fix_action || f.replacement;
      if (fix) lines.push(`- Suggested fix: ${flat(fix)}`);
    });
    lines.push("");
    lines.push(
      `For each finding, explain the concrete impact in this org and apply a fix (prefer the ` +
        `suggested fix / replacement where given). Prioritise Breaking, then Deprecated. ` +
        `Return the corrected ${g.componentType} as one complete code block.`,
    );
    const intent = lines.join("\n");
    // MultiAgentRoute caps user_intent at 4 000 chars (runs.py). Guard here so
    // the handoff fails loudly instead of silently truncating the prompt.
    if (intent.length > 3900) {
      toast.error(
        `The prompt for "${g.componentName}" is too large (${intent.length} chars). ` +
          `Try selecting fewer findings or using a shorter component name.`,
      );
      return;
    }
    setMultiAgentIntent(intent);
    navigate("/weave");
  };

  // Progress labels derived from unified pipeline stream
  const busyLabel = (() => {
    if (!pipeline.isActive) return null;
    if (pipeline.stage === "document_processing") return `Processing · ${pipeline.progress}%`;
    if (pipeline.stage === "scan_queued") return pipeline.elapsedSeconds > 0 ? `Scan queued · ${pipeline.elapsedSeconds}s` : "Scan queued…";
    if (pipeline.stage === "scan_starting") return "Scan starting…";
    if (pipeline.stage === "scan_running") return `Scanning · ${pipeline.progress}%`;
    return "Preparing…";
  })();

  const liveStatusLabel = busyLabel;
  const liveStatusPct = pipeline.isActive ? pipeline.progress : null;
  const liveStatusError = pipeline.error ?? null;

  return (
    <div className="flex flex-col space-y-4 md:space-y-6">
      {/* Action bar */}
      <div className="flex items-center justify-end flex-wrap gap-2">
        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.md,.html,.htm,application/pdf,text/markdown,text/html"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
            e.target.value = "";
          }}
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInput.current?.click()}
          disabled={upload.isPending || pipeline.isActive}
          className="gap-2"
        >
          {upload.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span>Upload PDF</span>
        </Button>

        {/* Cancel button — only visible while pipeline is active */}
        {pipeline.isActive && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={cancelMut.isPending}
            className="gap-2 border-destructive text-destructive hover:bg-destructive/10"
          >
            {cancelMut.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <X className="h-4 w-4" />}
            <span>{busyLabel ?? "Processing…"}</span>
            <span className="text-xs opacity-60">· Cancel</span>
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => doExport("csv")}
          disabled={!scanId || csvMut.isPending}
          className="gap-2"
        >
          {csvMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
          <span>Export CSV</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => doExport("json")}
          disabled={!scanId || jsonMut.isPending}
          className="gap-2"
        >
          {jsonMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
          <span>Export JSON</span>
        </Button>
      </div>

      {/* Overview — metrics + filter bar + severity breakdown */}
      <div ref={overviewRef} className="flex-shrink-0 space-y-4">
        {isInitialLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-border/60 bg-muted/20 px-4 py-3">
                <div className="loading-parallax mb-2 h-2.5 w-16 rounded bg-muted-foreground/10" />
                <div className="loading-parallax h-5 w-10 rounded bg-muted-foreground/[0.07]" />
              </div>
            ))}
          </div>
        ) : (
          <ImpactMetrics
            counts={normalizedBucketCounts}
            totalComponents={totalComponents}
          />
        )}

        {isInitialLoading ? (
          <div className="rounded-lg border border-border/70 bg-muted/20 px-4 py-3 flex flex-wrap items-center gap-3">
            <div className="loading-parallax h-3 w-12 rounded bg-muted-foreground/10" />
            <div className="loading-parallax h-9 w-44 rounded-md bg-muted-foreground/10" />
            <div className="loading-parallax h-9 w-56 rounded-md bg-muted-foreground/10" />
            <div className="loading-parallax ml-auto h-3 w-32 rounded bg-muted-foreground/10" />
          </div>
        ) : docs.length > 0 ? (
          <div className="rounded-lg border border-border/70 bg-muted/20 px-4 py-2.5 flex items-center gap-x-3 gap-y-2 flex-wrap">
              {/* ── Release picker ──────────────────────────── */}
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Release</span>
                <Select
                  value={activeReleaseId ?? undefined}
                  onValueChange={(v) => setActiveReleaseId(v)}
                  disabled={docs.length <= 1}
                >
                  {/* overflow-hidden prevents long PDF filenames from spilling out of the trigger */}
                  <SelectTrigger className="h-8 w-56 overflow-hidden">
                    <SelectValue placeholder="Pick a release doc">
                      <span
                        className={`block min-w-0 truncate text-left ${activeDoc ? "" : "text-muted-foreground"}`}
                        title={activeDoc?.filename}
                      >
                        {activeDoc?.filename ?? "Pick a release doc"}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-w-[22rem]">
                    {docs.map((d) => (
                      <SelectItem key={d.release_id} value={d.release_id}>
                        <span className="block max-w-[20rem] truncate" title={d.filename}>
                          {d.filename}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {activeDoc ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    title={`Delete "${activeDoc.filename}"`}
                    aria-label="Delete release document"
                    disabled={deleteMut.isPending}
                    onClick={() => void handleDeleteActiveDoc()}
                  >
                    {deleteMut.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                ) : null}
              </div>

              {/* ── Divider ─────────────────────────────────── */}
              <div className="h-5 w-px bg-border shrink-0" />

              {/* ── Filters ─────────────────────────────────── */}
              <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground shrink-0">Filter</span>
                <Select
                  value={selectedBucket}
                  onValueChange={(v) => { setSelectedBucket(v); setShowFindings(true); setPage(1); }}
                >
                  <SelectTrigger className="h-8 w-36">
                    <SelectValue placeholder="Bucket" />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    <SelectItem value="all">All buckets</SelectItem>
                    <SelectItem value="Breaking">Breaking</SelectItem>
                    <SelectItem value="Deprecated">Deprecated</SelectItem>
                    <SelectItem value="Review">Review</SelectItem>
                    <SelectItem value="Improvement">Improvement</SelectItem>
                    <SelectItem value="OK">OK</SelectItem>
                  </SelectContent>
                </Select>
                <MetadataTypeMultiSelect
                  options={componentTypes}
                  value={selectedTypes}
                  onChange={(next) => { setSelectedTypes(next); setShowFindings(true); setPage(1); }}
                  highlighted={defaultScanTypes}
                  defaultValue={[]}
                  className="w-52"
                />
                {(selectedBucket !== "all" || selectedTypes.length > 0) && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline hover:text-foreground shrink-0"
                    onClick={() => { setSelectedBucket("all"); setSelectedTypes([]); setPage(1); }}
                  >
                    clear
                  </button>
                )}
              </div>

              {/* ── Count ───────────────────────────────────── */}
              <span className="ml-auto text-xs text-muted-foreground shrink-0">
                {totalFindings} finding{totalFindings === 1 ? "" : "s"}
                {(selectedBucket !== "all" || selectedTypes.length > 0) ? " · filtered" : ""}
              </span>
          </div>
        ) : null}


        {isInitialLoading || heatmapLoading ? (
          <div className="rounded-xl border border-border bg-card px-5 py-4 space-y-3">
            <div className="loading-parallax h-4 w-72 rounded bg-muted-foreground/10" />
            <div className="flex items-center gap-1.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="loading-parallax h-6 w-20 rounded-full bg-muted-foreground/10" />
              ))}
            </div>
            <div className="space-y-1.5 pt-1">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="grid grid-cols-[minmax(120px,200px)_1fr_44px] gap-3 items-center">
                  <div className="loading-parallax h-5 w-full rounded bg-muted-foreground/10" />
                  <div className="loading-parallax h-6 w-full rounded bg-muted-foreground/[0.07]" />
                  <div className="loading-parallax ml-auto h-4 w-8 rounded bg-muted-foreground/10" />
                </div>
              ))}
            </div>
          </div>
        ) : docs.length > 0 ? (
          <ImpactSeverityRanked
            matrix={severityHeatmap}
            onCellClick={(bucket, type) => {
              setSelectedBucket(bucket);
              setSelectedTypes([type]);
              setShowFindings(true);
              setPage(1);
              requestAnimationFrame(() => {
                findingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }}
            onRowClick={(type) => {
              setSelectedBucket("all");
              setSelectedTypes([type]);
              setShowFindings(true);
              setPage(1);
              requestAnimationFrame(() => {
                findingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }}
            onColumnClick={(bucket) => {
              setSelectedBucket(bucket);
              setSelectedTypes([]);
              setShowFindings(true);
              setPage(1);
              requestAnimationFrame(() => {
                findingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }}
            onAllClick={() => {
              setSelectedBucket("all");
              setSelectedTypes([]);
              setShowFindings(true);
              setPage(1);
              requestAnimationFrame(() => {
                findingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              });
            }}
            activeBucket={selectedBucket}
            activeType={selectedTypeSingle}
            visibleTypes={selectedTypes.length > 0 ? selectedTypes : undefined}
          />
        ) : null}
      </div>

      {/* Drill-down */}
      {docs.length === 0 ? (
        <div className="rounded-xl border border-border bg-card py-16 flex flex-col items-center text-center gap-2">
          <Upload className="h-10 w-10 text-muted-foreground" />
          <p className="text-base font-medium">No release notes uploaded yet</p>
          <p className="text-sm text-muted-foreground">
            Click <span className="font-medium">Upload PDF</span> above to start.
          </p>
        </div>
      ) : !showFindings ? (
        <div className="rounded-xl border border-border bg-card px-5 py-6 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Click any bar, row, or bucket above to drill into specific components — or open the full findings table below.
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={!scanId}
            onClick={() => {
              setShowFindings(true);
              requestAnimationFrame(() =>
                findingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
              );
            }}
          >
            View all findings ({totalFindings})
          </Button>
        </div>
      ) : (
        <div ref={findingsRef} className="flex flex-col">
          <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-xs"
              onClick={() => {
                setShowFindings(false);
                setSelectedBucket("all");
                setSelectedTypes([]);
                setPage(1);
                requestAnimationFrame(() =>
                  overviewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
                );
              }}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back to overview
            </Button>
            <span className="h-4 w-px bg-border" />
            <span>Showing:</span>
            {selectedBucket !== "all" || selectedTypes.length > 0 ? (
              <>
                <span className="rounded bg-muted px-2 py-0.5 font-medium text-foreground">
                  {selectedBucket !== "all" ? selectedBucket : "all buckets"}
                  {selectedTypes.length === 1 ? ` · ${selectedTypes[0]}` :
                   selectedTypes.length > 1 ? ` · ${selectedTypes.length} types` : ""}
                </span>
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => { setSelectedBucket("all"); setSelectedTypes([]); setPage(1); }}
                >
                  clear filters
                </button>
              </>
            ) : (
              <span>all findings</span>
            )}
          </div>
          <div>
            <ReleaseImpactContent
              findings={visibleFindings}
              isLoading={findingsData.isFetching}
              componentTypes={componentTypes}
              searchTerm={searchTerm}
              selectedBucket={selectedBucket}
              selectedType={selectedTypeSingle}
              currentPage={page}
              pageSize={pageSize}
              totalPages={totalPages}
              totalComponents={totalComponents}
              totalFindings={totalFindings}
              onSearchChange={setSearchTerm}
              onBucketChange={setSelectedBucket}
              onTypeChange={(v) => setSelectedTypes(v === "all" ? [] : [v])}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              docs={undefined}
              activeReleaseId={activeReleaseId}
              onPickDoc={undefined}
              liveStatusLabel={liveStatusLabel}
              liveStatusPct={liveStatusPct}
              liveStatusError={liveStatusError}
              onImproveWithAI={improveWithAI}
              canImprove={canImprove}
              onImproveComponent={improveComponentWithAI}
            />
          </div>
        </div>
      )}
    </div>
  );
}
